/**
 * Секция выдачи/закрытия
 */

import React from 'react';
import { Formik, Form } from 'formik';
import { TextInput, Checkbox } from '@components/UI/FormItems';
import { ReactComponent as Print } from '../../../asssets/icons/print.svg';
import {
  useDownloadDealDocuments,
  useSetDealIssued,
  useRevokeAccount,
  useRevokeAccountApplication,
  useRejectCrif,
  useDeactivate9FZ,
  useSendDocumentsToIbso,
  useRevokeAccounts,
} from '@/hooks/useAdmin.ts';
import { ReviveApplicationFormComponent } from '../forms/ReviveApplicationForm';
import { UpdatePointOfSaleFormComponent } from '../forms/UpdatePointOfSaleForm';
import { validApplicationIdSchema } from '../validation/schemas';
import { FileUploadFormComponent } from '@views/AdminOffice/forms/FileUploadForm.tsx';

interface IssueSectionProps {
  creditContractId: number | null;
}

/**
 * Компонент секции "Выдача/Закрытие"
 */
export const IssueSection: React.FC<IssueSectionProps> = ({ creditContractId }) => {
  const downloadDocumentsMutation = useDownloadDealDocuments();
  const setDealIssuedMutation = useSetDealIssued();
  const revokeAccountMutation = useRevokeAccount();
  const revokeAccountApplicationMutation = useRevokeAccountApplication();
  const revokeAccountsMutation = useRevokeAccounts();
  const rejectCrifMutation = useRejectCrif();
  const deactivate9FZMutation = useDeactivate9FZ();
  const sendToIbsoMutation = useSendDocumentsToIbso();

  // Сбрасываем состояние всех мутаций при смене клиента
  React.useEffect(() => {
    downloadDocumentsMutation.reset();
    setDealIssuedMutation.reset();
    revokeAccountMutation.reset();
  }, [creditContractId]);

  return (
    <div>
      {/* Реанимация заявки */}
      <ReviveApplicationFormComponent />

      {/* Смена точки продаж */}
      <UpdatePointOfSaleFormComponent creditContractId={creditContractId} />

      {/* Скачивание документов */}
      <fieldset>
        <Formik initialValues={{}} onSubmit={() => {}}>
          {() => (
            <Form>
              <div style={{ display: 'table' }}>
                <div style={{ marginLeft: '5px', fontWeight: 'bold' }}>Скачивание документов</div>
                <button
                  className="btn"
                  type="button"
                  onClick={() => {
                    if (creditContractId) {
                      downloadDocumentsMutation.mutate(creditContractId);
                    }
                  }}
                  disabled={!creditContractId || downloadDocumentsMutation.isPending}
                  style={{ marginTop: '8px' }}
                >
                  Пакет документов сделки (без подписи) <Print className="icon" />
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </fieldset>

      {/* Установить статус "Выдан" */}
      <fieldset>
        <Formik
          initialValues={{}}
          onSubmit={async () => {
            if (creditContractId) {
              await setDealIssuedMutation.mutateAsync(creditContractId);
            }
          }}
        >
          {() => (
            <Form>
              <div style={{ display: 'table' }}>
                <div style={{ marginLeft: '5px', fontWeight: 'bold' }}>
                  Проставить сделку в статус &quot;Выдан&quot; и отправить документы в 1с
                </div>
                <button
                  className="btn"
                  type="submit"
                  disabled={!creditContractId || setDealIssuedMutation.isPending}
                  style={{ marginTop: '20px' }}
                >
                  {setDealIssuedMutation.isPending ? 'Обработка...' : 'Запустить процедуру'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </fieldset>

      {/* Снять авторизацию */}
      <fieldset>
        <Formik
          initialValues={{ bankrupt: false }}
          onSubmit={async (values) => {
            if (creditContractId) {
              await revokeAccountMutation.mutateAsync({
                creditContractId,
                bankrupt: values.bankrupt ?? false,
              });
            }
          }}
        >
          {() => (
            <Form>
              <div style={{ display: 'table' }}>
                <div style={{ marginLeft: '5px', fontWeight: 'bold' }}>
                  Снять авторизацию по основному счету Клиента
                </div>
                <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Checkbox name="bankrupt">Банкротство</Checkbox>
                </div>
                <button
                  className="btn"
                  type="submit"
                  disabled={!creditContractId || revokeAccountMutation.isPending}
                  style={{ marginTop: '10px' }}
                >
                  {revokeAccountMutation.isPending ? 'Обработка...' : 'Запустить процедуру'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </fieldset>

      {/* Снять авторизацию по заявке */}
      <fieldset>
        <Formik
          initialValues={{ applicationId: '', bankrupt: false }}
          validationSchema={validApplicationIdSchema}
          onSubmit={async (values, { resetForm }) => {
            const id = Number(values.applicationId);
            if (id) {
              await revokeAccountApplicationMutation.mutateAsync({
                applicationId: id,
                bankrupt: values.bankrupt ?? false,
              });
              resetForm();
            }
          }}
        >
          {() => (
            <Form>
              <div style={{ display: 'table' }}>
                <div style={{ marginLeft: '10px', fontWeight: 'bold' }}>
                  Отправить снятие авторизации ИБСО по id заявки
                </div>
                <div style={{ marginTop: '20px' }} />

                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
                  <Checkbox name="bankrupt">Банкротство</Checkbox>
                </div>

                <TextInput
                  label="id Заявки : "
                  name="applicationId"
                  placeholder="123456"
                  style={{ width: '100px' }}
                />

                <button
                  className="btn"
                  type="submit"
                  disabled={revokeAccountApplicationMutation.isPending}
                  style={{ marginLeft: '10px' }}
                >
                  {revokeAccountApplicationMutation.isPending
                    ? 'Отправка...'
                    : 'Отправить снятие авторизации'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </fieldset>

      {/* Закрытие счетов по заявкам из файла */}
      <FileUploadFormComponent
        title="Множественная отправка запроса на закрытие счета по заявкам"
        fileId="revokeApplicaitons"
        onSubmit={async (formData) => {
          await revokeAccountsMutation.mutateAsync(formData);
        }}
        isLoading={revokeAccountsMutation.isPending}
      />

      {/* Отказ в CRIF */}
      <fieldset>
        <Formik
          initialValues={{ applicationId: '' }}
          validationSchema={validApplicationIdSchema}
          onSubmit={async (values, { resetForm }) => {
            const id = Number(values.applicationId);
            if (id) {
              await rejectCrifMutation.mutateAsync(id);
              resetForm();
            }
          }}
        >
          {() => (
            <Form>
              <div style={{ display: 'table' }}>
                <div style={{ marginLeft: '10px', fontWeight: 'bold' }}>
                  Отправить отказ в CRIF по id заявки
                </div>
                <div style={{ marginTop: '20px' }} />

                <TextInput
                  label="id Заявки : "
                  name="applicationId"
                  placeholder="123456"
                  style={{ width: '100px' }}
                />

                <button
                  className="btn"
                  type="submit"
                  disabled={rejectCrifMutation.isPending}
                  style={{ marginLeft: '10px' }}
                >
                  {rejectCrifMutation.isPending ? 'Отправка...' : 'Отправить отказ'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </fieldset>

      {/* Деактивация 9-ФЗ */}
      <fieldset>
        <Formik
          initialValues={{ applicationId: '' }}
          validationSchema={validApplicationIdSchema}
          onSubmit={async (values, { resetForm }) => {
            const id = Number(values.applicationId);
            if (id) {
              await deactivate9FZMutation.mutateAsync(id);
              resetForm();
            }
          }}
        >
          {() => (
            <Form>
              <div style={{ display: 'table' }}>
                <div style={{ marginLeft: '10px', fontWeight: 'bold' }}>
                  Деактивация заявки по 9-ФЗ
                </div>
                <div style={{ marginTop: '20px' }} />

                <TextInput
                  label="id Заявки : "
                  name="applicationId"
                  placeholder="123456"
                  style={{ width: '100px' }}
                />

                <button
                  className="btn"
                  type="submit"
                  disabled={deactivate9FZMutation.isPending}
                  style={{ marginLeft: '10px' }}
                >
                  {deactivate9FZMutation.isPending ? 'Деактивация...' : 'Деактивировать'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </fieldset>

      {/* Отправить документы в ИБСО */}
      <fieldset>
        <Formik
          initialValues={{ applicationId: '' }}
          validationSchema={validApplicationIdSchema}
          onSubmit={async (values, { resetForm }) => {
            const id = Number(values.applicationId);
            if (id) {
              await sendToIbsoMutation.mutateAsync(id);
              resetForm();
            }
          }}
        >
          {() => (
            <Form>
              <div style={{ display: 'table' }}>
                <div style={{ marginLeft: '10px', fontWeight: 'bold' }}>
                  Отправить документы по заявке в ИБСО
                </div>
                <div style={{ marginTop: '20px' }} />

                <TextInput
                  label="id Заявки : "
                  name="applicationId"
                  placeholder="123456"
                  style={{ width: '100px' }}
                />

                <button
                  className="btn"
                  type="submit"
                  disabled={sendToIbsoMutation.isPending}
                  style={{ marginLeft: '10px' }}
                >
                  {sendToIbsoMutation.isPending ? 'Отправка...' : 'Отправить документы'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </fieldset>
    </div>
  );
};
